import React, { useEffect, useState } from "react";
import Page from "src/base/Page";
import { Card, Col, Container, Form, FormGroup, Input, InputGroup, InputGroupText, Label, Row } from "reactstrap";
import EBSSSProfileHeader from "src/base/atoms/EBSSSProfileHeader";
import { useSelector } from "react-redux";
import { ReduxDataKey } from "src/base/store/store";
import { Session } from "src/base/store/authSlice";
import { useEBSSS } from "src/base/context/EBSSSContext/useEBSSS";
import { useEBSSSLayout } from "src/base/context/EBSSSLayoutContext/useEBSSSLayout";
import TreeView, { flattenTree } from "react-accessible-treeview";
import { remotePath } from "src/base/settings/Paths";
import { Image } from "src/AbstractElements";

import { Viewer } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import pdfWorker from "pdfjs-dist/build/pdf.worker.entry";
import { Worker } from "@react-pdf-viewer/core";
import { CSS } from "src/_utils/styles";

const workerUrl = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

function invertFilesAndGroups(files: any) {
  const result = {};

  files?.forEach((file) => {
    const group = file.group;

    if (!group) {
      // Skip files without a group
      return;
    }

    const groupId = group.id;

    // Initialize group in result if not already present
    if (!result[groupId]) {
      result[groupId] = {
        name: group.name,
        children: [],
      };
    }

    file["name"] = file["filename"];

    // Add the file to the group's files array
    result[groupId].id = Math.ceil(Math.random() * 1000000);
    result[groupId].children.push({ ...file, metadata: file });
  });

  return Object.values(result);
}

const FileViewer = ({ leadFile, trans }) => {
  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  const calculateFileSize = (size) => {
    return size / 1000 < 1000 ? `${Math.floor(size / 1000)}Kb` : `${Math.floor(size / 1000000)}Mb`;
  };

  const isImage = (hashname) => {
    const lowerHash = hashname?.toLowerCase();
    return lowerHash?.endsWith("png") || lowerHash?.endsWith("jpg") || lowerHash?.endsWith("jpeg");
  };

  return (
    <section className="row">
      <div className="col-12">
        <h3 className="mb-0 d-inline-block">{leadFile.filename}</h3>
        <a
          href={remotePath.clientInstanceUrl() + `/crm/viewer/${leadFile.hashname}`}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-primary pull-right"
        >
          <i className="la la-download"></i> {trans("common.download")}
        </a>
      </div>

      <div className="col-12 d-flex flex-column">
        {leadFile.description && (
          <>
            <label>{trans("common.description")}</label>
            <p dangerouslySetInnerHTML={{ __html: leadFile.description }} />
            <hr />
          </>
        )}

        <label>{trans("website.document-size")}:</label>
        {" " + calculateFileSize(leadFile.size)}
        <hr />

        <label>{trans("common.created_at")}:</label>
        {" " + leadFile.created_at}
        <hr />

        {leadFile.available_from && (
          <small>
            {trans("website.file-available-from")}: {leadFile.available_from}
          </small>
        )}
        {leadFile.available_to && (
          <small>
            {trans("website.file-available-to")}: {leadFile.available_to}
          </small>
        )}
        {leadFile.start_at && (
          <small>
            {trans("website.file-start-at")}: {leadFile.start_at}
          </small>
        )}
        {leadFile.end_at && (
          <small>
            {trans("website.file-end-at")}: {leadFile.end_at}
          </small>
        )}
        {leadFile.action_date && (
          <small>
            {trans("website.file-action-date")}: {leadFile.action_date}
            <br />
            {leadFile.hours_to_notify && (
              <>
                {" "}
                {trans("website.file-sending-notification-before")}: {leadFile.hours_to_notify} {trans("common.hours")}
              </>
            )}
          </small>
        )}
      </div>

      <div className="col-12 mt-3" style={{ border: "10px solid #EEE" }}>
        {isImage(leadFile.hashname) ? (
          <Image
            attrImage={{
              src: remotePath.assetsUrl(leadFile.filepath),
              alt: "File preview",
              style: { width: "100%" },
            }}
          />
        ) : (
          <div style={CSS({ height: 1000 })}>
            <Worker workerUrl={workerUrl}>
              <Viewer fileUrl={remotePath.assetsUrl(leadFile.filepath)} plugins={[defaultLayoutPluginInstance]} />
            </Worker>
          </div>
        )}
      </div>
    </section>
  );
};

const UserFilesPage = () => {
  const { files, entityFiles, fetchFiles } = useEBSSS();
  const { trans } = useEBSSSLayout();
  const sessionNow: Session = useSelector((state) => state[ReduxDataKey.AUTH]);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    fetchFiles();
  }, []);

  const folder = {
    name: "",
    children: invertFilesAndGroups(files) as any,
  };

  const data = flattenTree(folder);

  return (
    <Page breads={[{ name: trans("common.files"), active: true }]}>
      <Container fluid={true}>
        <div className="user-profile">
          <Row>
            <EBSSSProfileHeader user={sessionNow?.user} />
            <Col xl="4" md="5" lg="12">
              <div className="default-according">
                <div className="app-content content">
                  <div className="content-wrapper">
                    <div className="content-body">
                      <section className="row">
                        <div className="col-12">
                          <div className="card">
                            <div className="card-content">
                              {files && files?.length > 0 && (
                                <div className="card-body">
                                  <TreeView
                                    data={data}
                                    className="basic"
                                    aria-label="basic example tree"
                                    nodeRenderer={({ element, getNodeProps, level, handleSelect }) => (
                                      <div
                                        {...getNodeProps()}
                                        className={`${getNodeProps().className} py-3 border-2 border`}
                                        style={{ paddingLeft: `${level === 1 ? 10 : 35 * (level - 1)}px`, cursor: "pointer" }}
                                        onClick={(e) => {
                                          getNodeProps().onClick(e);
                                          if (level > 1) {
                                            setSelectedFile(element.metadata);
                                          }
                                        }}
                                      >
                                        {level === 1 && <i className="fa fa-folder-open me-2"></i>}
                                        {element.name}
                                      </div>
                                    )}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </section>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
            <Col xl="8" md="7" lg="12">
              {selectedFile ? <FileViewer leadFile={selectedFile} trans={trans} /> : <></>}
            </Col>
          </Row>
        </div>
      </Container>
    </Page>
  );
};

export default UserFilesPage;
